import React, { useState } from "react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import LanguageToggle from "../components/LanguageToggle";
import { InstallPWAButton } from "../components/InstallPWAButton";
import { formatApiError } from "../api";
import { Package, Eye, EyeSlash } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("expired") === "1";
  const resetSuccess = searchParams.get("reset") === "1";
  // v406.7 — deep links (e.g. "Fulfill with Bulk Give" email button) survive
  // the login wall: Protected passes the intended destination via state.
  const dest = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    setTimeout(() => nav(dest), 0);
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success(t("auth.welcome_back"));
      nav(dest);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-8">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4 gap-2">
          <InstallPWAButton />
          <LanguageToggle />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white border border-[#c9a24a] rounded-xl shadow-sm flex items-center justify-center">
              <Package size={28} weight="bold" color="#c9a24a" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">ReUP</h1>
          </div>
          <p className="text-sm sm:text-base text-zinc-600">
            {t("auth.tagline")}
          </p>
        </div>

        <BrutalCard data-testid="login-card">
          <h2 className="text-2xl font-semibold mb-6 tracking-tight">{t("auth.log_in")}</h2>
          {sessionExpired && (
            <div
              className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 text-sm text-amber-900"
              data-testid="session-expired-banner"
            >
              {t("auth.session_expired")}
            </div>
          )}
          {resetSuccess && (
            <div
              className="mb-4 flex items-start gap-2 bg-emerald-50 border border-emerald-300 rounded-lg px-3 py-2.5 text-sm text-emerald-900"
              data-testid="reset-success-banner"
            >
              {t("auth.reset_success_banner")}
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t("common.email")}</label>
              <BrutalInput
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t("common.password")}</label>
              <div className="relative">
                <BrutalInput
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#c9a24a] transition-colors p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={22} /> : <Eye size={22} />}
                </button>
              </div>
            </div>
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-[#8a6a1c] hover:underline"
                data-testid="forgot-password-link"
              >
                {t("auth.forgot_password")}
              </Link>
            </div>
            <BrutalButton
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full"
              data-testid="login-submit-button"
            >
              {loading ? t("auth.logging_in") : t("auth.log_in")}
            </BrutalButton>
          </form>
          <p className="text-sm text-zinc-600 mt-6 text-center">
            {t("auth.got_invite")}
          </p>
        </BrutalCard>
      </div>
    </div>
  );
}
